import { z } from 'zod';

import { addressSchema } from '../address';
import { currencySchema } from '../currency';
import { menuCategoryOwnerSchema, menuCategoryTranslationsSchema } from '../menuCategory';
import { menuItemOwnerSchema, menuItemTranslationsSchema } from '../menuItem';
import { dailyOpeningHoursSchema } from '../openingHours';
import { payoutScheduleSchema } from '../payoutSchedule';
import { emailSchema, minorUnitsBigintSchema, phoneSchema, ulidSchema } from '../primitives';
import { cuisineSchema, restaurantOwnerSchema, restaurantTranslationsSchema } from '../restaurant';
import type { ownerCreateVoucherRequestSchema, patchVoucherEnabledRequestSchema } from '../voucher';

export const restaurantOnboardRequestSchema = z.object({
  name: z.string().min(1).max(140).optional(),
  cuisines: z.array(cuisineSchema).max(5).optional(),
  /** Shifts default map coordinates when creating multiple test outlets in one run. */
  coordinateIndex: z.number().int().nonnegative().max(1_000).optional(),
});
export type RestaurantOnboardRequest = z.infer<typeof restaurantOnboardRequestSchema>;

export const restaurantOnboardResponseSchema = z.object({
  restaurantId: z.string(),
  status: z.literal('DRAFT'),
});
export type RestaurantOnboardResponse = z.infer<typeof restaurantOnboardResponseSchema>;

export const patchRestaurantRequestSchema = z
  .object({
    name: z.string().min(1).max(140).optional(),
    currency: currencySchema.optional(),
    address: addressSchema.optional(),
    contactEmail: emailSchema.optional(),
    contactPhone: phoneSchema.optional(),
    cuisines: z.array(cuisineSchema).max(5).optional(),
    offersRestaurantDelivery: z.boolean().optional(),
    restaurantDeliveryRadiusKilometers: z.number().nonnegative().optional(),
    acceptsPickup: z.boolean().optional(),
    allowsPlatformDelivery: z.boolean().optional(),
    /** Owner toggle while `APPROVED` or `PAUSED` (no other draft fields in the same request). */
    acceptingOrders: z.boolean().optional(),
    translations: restaurantTranslationsSchema.optional(),
  })
  .strict()
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'At least one field is required',
  })
  .superRefine((body, ctx) => {
    const keys = Object.keys(body).filter((k) => body[k as keyof typeof body] !== undefined);
    if (keys.includes('acceptingOrders') && keys.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'acceptingOrders must be the only field when updating availability for an APPROVED or PAUSED restaurant',
        path: ['acceptingOrders'],
      });
    }
  });
export type PatchRestaurantRequest = z.infer<typeof patchRestaurantRequestSchema>;

export const patchRestaurantResponseSchema = z.object({
  restaurantId: z.string(),
  infoVersion: z.number().int().nonnegative(),
});
export type PatchRestaurantResponse = z.infer<typeof patchRestaurantResponseSchema>;

export const postMenuItemRequestSchema = z.object({
  name: z.string().min(1).max(140),
  description: z.string().max(2000).optional(),
  category: z.string().max(80).optional(),
  menuCategoryId: ulidSchema.optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  priceMinorUnits: minorUnitsBigintSchema,
  currency: currencySchema,
  available: z.boolean().optional(),
  translations: menuItemTranslationsSchema.optional(),
});
export type PostMenuItemRequest = z.infer<typeof postMenuItemRequestSchema>;

export const postMenuItemResponseSchema = z.object({
  menuItemId: z.string(),
  restaurantId: z.string(),
});
export type PostMenuItemResponse = z.infer<typeof postMenuItemResponseSchema>;

export const putOpeningHoursRequestSchema = z.object({
  weeklyHours: z.array(dailyOpeningHoursSchema).min(1),
});
export type PutOpeningHoursRequest = z.infer<typeof putOpeningHoursRequestSchema>;

export const putOpeningHoursResponseSchema = z.object({
  restaurantId: z.string(),
});
export type PutOpeningHoursResponse = z.infer<typeof putOpeningHoursResponseSchema>;

export const deliveryPricingTierWireSchema = z.object({
  minOrderValueMinorUnits: minorUnitsBigintSchema,
  deliveryFeeMinorUnits: minorUnitsBigintSchema,
});
export type DeliveryPricingTierWire = z.infer<typeof deliveryPricingTierWireSchema>;

/** Response-side variant: minor unit fields serialized as strings for safe JSON transport. */
export const deliveryPricingTierResponseSchema = z.object({
  minOrderValueMinorUnits: z.string(),
  deliveryFeeMinorUnits: z.string(),
});
export type DeliveryPricingTierResponse = z.infer<typeof deliveryPricingTierResponseSchema>;

export const putDeliveryPricingRequestSchema = z.object({
  tiers: z.array(deliveryPricingTierWireSchema).min(1),
});
export type PutDeliveryPricingRequest = z.infer<typeof putDeliveryPricingRequestSchema>;

export const putDeliveryPricingResponseSchema = z.object({
  restaurantId: z.string(),
});
export type PutDeliveryPricingResponse = z.infer<typeof putDeliveryPricingResponseSchema>;

export const putOwnerPayoutConfigRequestSchema = z.object({
  payoutSchedule: payoutScheduleSchema,
  payoutMinimumMinorUnits: minorUnitsBigintSchema,
});
export type PutOwnerPayoutConfigRequest = z.infer<typeof putOwnerPayoutConfigRequestSchema>;

export const putOwnerPayoutConfigResponseSchema = z.object({
  restaurantId: z.string(),
  infoVersion: z.number().int().nonnegative(),
});
export type PutOwnerPayoutConfigResponse = z.infer<typeof putOwnerPayoutConfigResponseSchema>;

export const restaurantOwnerDetailResponseSchema = z.object({
  restaurant: restaurantOwnerSchema,
  menuCategories: z.array(menuCategoryOwnerSchema),
  menuItems: z.array(menuItemOwnerSchema),
  openingHours: z.array(dailyOpeningHoursSchema),
  deliveryTiers: z.array(deliveryPricingTierResponseSchema),
});
export type RestaurantOwnerDetailResponse = z.infer<typeof restaurantOwnerDetailResponseSchema>;

export const postMenuCategoryRequestSchema = z.object({
  name: z.string().min(1).max(120),
  sortOrder: z.number().int().nonnegative().optional(),
  translations: menuCategoryTranslationsSchema.optional(),
});
export type PostMenuCategoryRequest = z.infer<typeof postMenuCategoryRequestSchema>;

export const postMenuCategoryResponseSchema = z.object({
  menuCategoryId: ulidSchema,
  restaurantId: ulidSchema,
});
export type PostMenuCategoryResponse = z.infer<typeof postMenuCategoryResponseSchema>;

export const patchMenuCategoryRequestSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    sortOrder: z.number().int().nonnegative().optional(),
    translations: menuCategoryTranslationsSchema.optional(),
  })
  .strict()
  .refine(
    (b) => b.name !== undefined || b.sortOrder !== undefined || b.translations !== undefined,
    { message: 'At least one field is required' },
  );
export type PatchMenuCategoryRequest = z.infer<typeof patchMenuCategoryRequestSchema>;

export const menuCategoryPathParamsSchema = z.object({
  id: ulidSchema,
  /** Matches Dynamo `CATEGORY#<id>` row. */
  categoryId: ulidSchema,
});
export type MenuCategoryPathParams = z.infer<typeof menuCategoryPathParamsSchema>;

export const putMenuCategoriesOrderRequestSchema = z.object({
  categoryIds: z.array(ulidSchema).min(1),
});
export type PutMenuCategoriesOrderRequest = z.infer<typeof putMenuCategoriesOrderRequestSchema>;

export const menuItemPathParamsSchema = z.object({
  id: ulidSchema,
  menuItemId: ulidSchema,
});
export type MenuItemPathParams = z.infer<typeof menuItemPathParamsSchema>;

export const patchMenuItemResponseSchema = menuItemOwnerSchema;
export type PatchMenuItemResponse = z.infer<typeof patchMenuItemResponseSchema>;

export const patchMenuItemRequestSchema = z
  .object({
    name: z.string().min(1).max(140).optional(),
    description: z.string().max(2000).optional(),
    category: z.string().max(80).optional(),
    menuCategoryId: ulidSchema.nullable().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
    priceMinorUnits: minorUnitsBigintSchema.optional(),
    currency: currencySchema.optional(),
    available: z.boolean().optional(),
    translations: menuItemTranslationsSchema.optional(),
  })
  .strict()
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: 'At least one field is required',
  });
export type PatchMenuItemRequest = z.infer<typeof patchMenuItemRequestSchema>;

export const putMenuItemsOrderRequestSchema = z.object({
  menuCategoryId: ulidSchema.optional(),
  menuItemIds: z.array(ulidSchema).min(1),
});
export type PutMenuItemsOrderRequest = z.infer<typeof putMenuItemsOrderRequestSchema>;

export const menuMutationVersionResponseSchema = z.object({
  restaurantId: ulidSchema,
  menuVersion: z.number().int().nonnegative(),
});
export type MenuMutationVersionResponse = z.infer<typeof menuMutationVersionResponseSchema>;

export const submitForReviewResponseSchema = z.object({
  restaurantId: z.string(),
  status: z.literal('PENDING_REVIEW'),
});
export type SubmitForReviewResponse = z.infer<typeof submitForReviewResponseSchema>;

export const ownerRestaurantVoucherPathParamsSchema = z.object({
  id: ulidSchema,
  voucherId: ulidSchema,
});
export type OwnerRestaurantVoucherPathParams = z.infer<
  typeof ownerRestaurantVoucherPathParamsSchema
>;

export type OwnerCreateVoucherRequest = z.infer<typeof ownerCreateVoucherRequestSchema>;
export type PatchVoucherEnabledRequest = z.infer<typeof patchVoucherEnabledRequestSchema>;
